// src/pages/MyTeamPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMyTeam, clearError } from 'store/slices/teamSlice';
import { Clock, Users, Search, MoreVertical, Calendar, User } from 'lucide-react';
import DataTable from 'components/common/DataTable';
import PageHeader from 'components/common/PageHeader';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'components/ui/table';

import usePermissionSocket from 'hooks/usePermissionSocket';
import { formatDateTime } from 'utlis/formateDateTime';

const MyTeamPage = () => {
    // usePermissionSocket();
    const dispatch = useDispatch();
    const { members, pagination, isLoading, error } = useSelector((state) => state.team);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMember, setSelectedMember] = useState(null);
    const [isBreakModalOpen, setIsBreakModalOpen] = useState(false);

    const refetch = useCallback(() => {
        dispatch(fetchMyTeam({ page, pageSize, search: searchTerm }));
    }, [dispatch, page, pageSize, searchTerm]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const columns = [
        {
            accessorKey: 'full_name',
            header: 'Name',
            cell: ({ row }) => (
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <p className="font-medium">{row.original.full_name}</p>
                        <p className="text-sm text-muted-foreground">{row.original.email}</p>
                    </div>
                </div>
            ),
        },
        {
            accessorKey: 'code',
            header: 'Employee Code',
            cell: ({ row }) => <span className="font-mono font-semibold">{row.original.code}</span>,
        },
        {
            accessorKey: 'contact_no',
            header: 'Contact',
            cell: ({ row }) => row.original.contact_no || <span className="text-muted-foreground">—</span>,
        },
        // {
        //     accessorKey: 'brake_time_logs',
        //     header: 'Total Breaks',
        //     cell: ({ row }) => (
        //         <Badge variant="outline">
        //             {row.original.brake_time_logs?.length || 0} today
        //         </Badge>
        //     ),
        // },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const member = row.original;
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setSelectedMember(member);
                            setIsBreakModalOpen(true);
                        }}
                    >
                        <Clock className="h-4 w-4 mr-2" />
                        View Break Time
                    </Button>
                );
            },
        },
    ];

    return (
        <div className="space-y-6">
            {members?.length !== 0 && <PageHeader
                title="My Team"
                description="View your team members and their break logs"
                icon={<Users className="h-8 w-8" />}
            />}

            {/* <div className="mb-6">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search team members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div> */}

            {members?.length === 0 ? <div className="h-[85vh] flex items-center justify-center bg-background">
                <div className="flex flex-col items-center justify-center py-20 px-8 text-center max-w-2xl">
                    <div className="relative">
                        {/* Gradient ring */}
                        <div className="absolute inset-0 blur-xl bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-full scale-150" />

                        {/* Main icon */}
                        <div className="relative w-24 h-24 rounded-full bg-card/80 backdrop-blur-sm border border-border/50 shadow-xl flex items-center justify-center">
                            <Users className="w-12 h-12 text-primary" />
                        </div>
                    </div>

                    <h3 className="mt-8 text-2xl font-bold text-foreground leading-tight">
                        Currently, no team has been assigned to you.
                    </h3>

                    <p className="mt-4 text-muted-foreground text-lg">
                        Your administrator will assign you to a team soon. Once that happens, you’ll see your teammates here.
                    </p>
                </div>
            </div> :
                <DataTable
                    data={members}
                    totalCount={pagination.count}
                    columns={columns}
                    pageIndex={page - 1}
                    pageSize={pageSize}
                    pageCount={Math.ceil(pagination?.count / pagination?.page_size)}
                    onPageChange={(idx) => setPage(idx + 1)}
                    onPageSizeChange={setPageSize}
                    onSearch={setSearchTerm}
                    isLoading={isLoading}
                    searchPlaceholder="Search team..."
                    showExport={false}
                    showImport={false}
                    showSearch={false}
                />
            }

            {/* BREAK TIME MODAL */}
            <Dialog open={isBreakModalOpen} onOpenChange={setIsBreakModalOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold">
                            Break Time Logs — {selectedMember?.full_name}
                        </DialogTitle>
                        <p className="text-muted-foreground">
                            Employee Code: <span className="font-mono">{selectedMember?.code}</span>
                        </p>
                    </DialogHeader>

                    {selectedMember?.brake_time_logs?.length > 0 ? (
                        <div className="mt-6">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Title</TableHead>
                                        <TableHead>Punch In</TableHead>
                                        <TableHead>Punch Out</TableHead>
                                        <TableHead>Duration</TableHead>
                                        <TableHead>Reason</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {selectedMember.brake_time_logs.map((log) => {
                                        const punchInTime = log?.punch_in ? new Date(log.punch_in) : null;
                                        const punchOutTime = log?.punch_out ? new Date(log.punch_out) : null;

                                        const punchIn = punchInTime ? formatDateTime(punchInTime) : '-';
                                        const punchOut = punchOutTime ? formatDateTime(punchOutTime) : '-';

                                        const duration = punchInTime && punchOutTime
                                            ? (() => {
                                                const diffSeconds = Math.floor((punchOutTime - punchInTime) / 1000);

                                                const hours = Math.floor(diffSeconds / 3600);
                                                const minutes = Math.floor((diffSeconds % 3600) / 60);
                                                const seconds = diffSeconds % 60;

                                                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                                            })()
                                            : 'Ongoing';



                                        return (
                                            <TableRow key={log.id}>
                                                <TableCell className="font-medium">
                                                    <Badge variant={log.title === 'morning_break' ? 'default' : 'secondary'}>
                                                        {log.title_display || log.title}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>{punchIn}</TableCell>
                                                <TableCell>
                                                    {punchOut ? punchOut : <Badge variant="outline">Active</Badge>}
                                                </TableCell>
                                                <TableCell className="font-medium">{duration}</TableCell>
                                                <TableCell>
                                                    {log.not_on_desk_reason || <span className="text-muted-foreground">—</span>}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground">
                            <Clock className="h-16 w-16 mx-auto mb-4 opacity-50" />
                            <p className="text-lg">No break logs found for today</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MyTeamPage;